/**
 * SearchableTagInput - A search-driven tag selection component.
 *
 * Replaces overwhelming tag grids with a focused search interface:
 * - Search input with autocomplete dropdown
 * - Only selected items shown as removable tags
 * - Supports custom value addition
 * - Full ARIA compliance via Headless UI Combobox
 *
 * Used in:
 * - ExposureAlertsCard (ports and protocols selection)
 */

import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';

import { RemovableDashedTag } from '@/components/RemovableDashedTag';
import { cn } from '@/utils/classname';

export interface Suggestion {
  value: string;
  label: string;
  description?: string;
}

export interface SearchableTagInputProps {
  /**
   * Currently selected values
   */
  selectedValues: string[];
  /**
   * Available suggestions for autocomplete
   */
  suggestions: Suggestion[];
  /**
   * Placeholder text for search input
   */
  placeholder: string;
  /**
   * Called when a value is added (from suggestion or custom)
   */
  onAdd: (value: string) => Promise<void>;
  /**
   * Called when a value is removed
   */
  onRemove: (value: string) => Promise<void>;
  /**
   * Whether the component is disabled
   */
  disabled?: boolean;
  /**
   * Test ID prefix for data-testid attributes
   */
  testId?: string;
  /**
   * Whether to hide the selected tag pills below the input.
   * Useful when the parent renders its own list of selected items.
   */
  hideSelectedTags?: boolean;
}

/**
 * SearchableTagInput provides a search-driven interface for selecting multiple values.
 *
 * Uses Headless UI Combobox for accessibility compliance:
 * - Keyboard navigation (arrow keys, enter, escape)
 * - Screen reader support
 * - Focus management
 *
 * @example
 * ```tsx
 * <SearchableTagInput
 *   selectedValues={['22', '443']}
 *   suggestions={PORT_SUGGESTIONS}
 *   placeholder="Search ports..."
 *   onAdd={async (value) => await addPort(value)}
 *   onRemove={async (value) => await removePort(value)}
 * />
 * ```
 */
export function SearchableTagInput({
  selectedValues,
  suggestions,
  placeholder,
  onAdd,
  onRemove,
  disabled,
  testId = 'searchable-tag-input',
  hideSelectedTags = false,
}: SearchableTagInputProps) {
  const [query, setQuery] = useState('');

  // Filter suggestions based on query (case-insensitive)
  const filteredSuggestions =
    query === ''
      ? suggestions
      : suggestions.filter(
          s =>
            s.value.toLowerCase().includes(query.toLowerCase()) ||
            s.label.toLowerCase().includes(query.toLowerCase()) ||
            s.description?.toLowerCase().includes(query.toLowerCase())
        );

  // Check if query matches any existing suggestion exactly
  const hasExactMatch = suggestions.some(s => s.value === query);

  // Check if query is already selected
  const isQueryAlreadySelected = selectedValues.includes(query);

  // Handle selection from dropdown
  const handleSelect = async (value: string | null) => {
    if (value && !selectedValues.includes(value)) {
      await onAdd(value);
      setQuery('');
    }
  };

  // Handle tag removal
  const handleRemove = async (value: string) => {
    await onRemove(value);
  };

  return (
    <div className="flex flex-col gap-3" data-testid={testId}>
      {/* Combobox Input with Dropdown */}
      <Combobox value={null} onChange={handleSelect} disabled={disabled}>
        <div className="relative">
          <MagnifyingGlassIcon className="text-chariot-text-tertiary absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <ComboboxInput
            className={cn(
              'border-chariot-stroke bg-chariot-background-secondary w-full rounded-lg border px-3 py-2.5 pl-10 text-sm',
              'placeholder:text-chariot-text-tertiary',
              'focus:border-chariot-brand-primary focus:ring-chariot-brand-primary focus:ring-1 focus:outline-hidden',
              disabled && 'cursor-not-allowed opacity-50'
            )}
            placeholder={placeholder}
            onChange={e => setQuery(e.target.value)}
            value={query}
            data-testid={`${testId}-input`}
          />
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-3">
            <ChevronDownIcon
              className="text-chariot-text-tertiary size-4"
              aria-hidden="true"
            />
          </ComboboxButton>

          <ComboboxOptions
            anchor="bottom"
            className={cn(
              // z-[200] keeps the dropdown above the Radix modal overlay (z-[100])
              // when rendered inside a Dialog (e.g., Exposure Alert Settings).
              'border-chariot-stroke bg-chariot-background-primary z-[200] mt-1 w-[var(--input-width)] rounded-lg border shadow-lg',
              'max-h-64 overflow-y-auto empty:hidden'
            )}
          >
            {filteredSuggestions.map(suggestion => (
              <ComboboxOption
                key={suggestion.value}
                value={suggestion.value}
                className={({ focus }) =>
                  cn(
                    'flex cursor-pointer items-center justify-between px-3 py-2.5',
                    focus && 'bg-chariot-background-secondary',
                    selectedValues.includes(suggestion.value) && 'opacity-50'
                  )
                }
              >
                <span className="text-chariot-text-primary text-sm font-medium">
                  {suggestion.label}
                </span>
                {suggestion.description && (
                  <span className="text-chariot-text-tertiary text-xs">
                    {suggestion.description}
                  </span>
                )}
              </ComboboxOption>
            ))}

            {/* Allow custom value when query doesn't match existing suggestions */}
            {query && !hasExactMatch && !isQueryAlreadySelected && (
              <ComboboxOption
                value={query}
                className={({ focus }) =>
                  cn(
                    'border-chariot-stroke bg-chariot-background-secondary text-chariot-text-secondary cursor-pointer border-t px-3 py-2 text-xs',
                    focus && 'bg-chariot-background-tertiary'
                  )
                }
              >
                Add custom: &quot;{query}&quot;
              </ComboboxOption>
            )}

            {/* Empty state when no matches */}
            {filteredSuggestions.length === 0 && !query && (
              <div className="text-chariot-text-tertiary px-3 py-2 text-sm">
                Type to search...
              </div>
            )}
          </ComboboxOptions>
        </div>
      </Combobox>

      {/* Selected Tags */}
      {!hideSelectedTags &&
        (selectedValues.length > 0 ? (
          <div
            className="flex flex-wrap gap-2"
            data-testid={`${testId}-selected`}
          >
            {selectedValues.map(value => {
              const suggestion = suggestions.find(s => s.value === value);
              const displayLabel = suggestion?.label || value;

              return (
                <RemovableDashedTag
                  key={value}
                  label={displayLabel}
                  size="sm"
                  onRemove={() => handleRemove(value)}
                />
              );
            })}
          </div>
        ) : (
          <p className="text-chariot-text-tertiary text-sm italic">
            No items selected
          </p>
        ))}
    </div>
  );
}
